import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import RxFollowUpService from "../../services/rx-follow-up.service";
import "./index.scss";

const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp,application/pdf";

const isImage = (mimeType) => `${mimeType || ""}`.startsWith("image/");

const formatDateTime = (value) =>
  value ? moment(value).format("MMM DD, YYYY hh:mm A") : "-";

const RxFollowUpTab = ({ requestId }) => {
  const currentUser = useSelector((state) => state.currentUser);
  const fileInputRef = useRef(null);

  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [note, setNote] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingNote, setEditingNote] = useState("");

  const loadEntries = useCallback(async () => {
    if (!requestId) return;
    setIsLoading(true);
    const response = await RxFollowUpService.GetByRequestId(requestId);
    setEntries(Array.isArray(response?.data) ? response.data : []);
    setIsLoading(false);
  }, [requestId]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const uploadHandler = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setIsUploading(true);
    for (const file of files) {
      try {
        const uploaded = await RxFollowUpService.UploadAttachment(file);
        setPendingAttachments((current) => [
          ...current,
          { ...uploaded, previewUrl: isImage(file.type) ? URL.createObjectURL(file) : null },
        ]);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Upload failed",
          text: `${file.name} could not be uploaded. Only images and PDFs up to 10MB are allowed.`,
        });
      }
    }
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removePendingAttachment = (fileKey) => {
    setPendingAttachments((current) => current.filter((item) => item.fileKey !== fileKey));
  };

  const saveHandler = async () => {
    if (!note.trim()) return;
    setIsSaving(true);
    const response = await RxFollowUpService.Create(
      requestId,
      note.trim(),
      pendingAttachments.map(({ fileKey, fileName, mimeType, fileSize }) => ({
        fileKey,
        fileName,
        mimeType,
        fileSize,
      }))
    );
    setIsSaving(false);
    if (response?.error) return;

    setNote("");
    setPendingAttachments([]);
    await loadEntries();
  };

  const startEditing = (entry) => {
    setEditingId(entry.Id);
    setEditingNote(entry.Note || "");
  };

  const saveEditing = async () => {
    if (!editingNote.trim()) return;
    const response = await RxFollowUpService.Update(editingId, editingNote.trim());
    if (response?.error) return;
    setEditingId(null);
    setEditingNote("");
    await loadEntries();
  };

  const deleteHandler = async (entry) => {
    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Delete this follow-up?",
      text: "The entry is removed from the thread but stays in the history.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc3545",
    });
    if (!confirmation.isConfirmed) return;

    const response = await RxFollowUpService.Delete(entry.Id);
    if (response?.error) return;
    await loadEntries();
  };

  const downloadPdf = async () => {
    setIsDownloading(true);
    try {
      await RxFollowUpService.DownloadRequestPdf(requestId);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Could not generate the PDF" });
    }
    setIsDownloading(false);
  };

  const canModify = (entry) =>
    currentUser?.user?.roleId === 1 ||
    Number(entry.CreatedByUserId) === Number(currentUser?.user?.id);

  return (
    <div className="rx-follow-up">
      <div className="card mb-3 d-print-none">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>Add Follow-up</span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={downloadPdf}
            disabled={isDownloading}
          >
            <i className="fa-solid fa-file-pdf me-2"></i>
            {isDownloading ? "Preparing..." : "Save as PDF"}
          </button>
        </div>
        <div className="card-body">
          <textarea
            className="form-control"
            rows={4}
            placeholder="Write what happened on this Rx: calls made, prescriber responses, insurance notes..."
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />

          <div className="rx-follow-up__composer-actions">
            <label className="btn btn-outline-secondary btn-sm mb-0">
              <i className="fa-solid fa-paperclip me-2"></i>
              Attach image or PDF
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                multiple
                accept={ACCEPTED_TYPES}
                onChange={uploadHandler}
              />
            </label>
            {isUploading && <span className="text-muted small">Uploading...</span>}
            <button
              type="button"
              className="btn btn-primary btn-sm ms-auto"
              onClick={saveHandler}
              disabled={isSaving || isUploading || !note.trim()}
            >
              {isSaving ? "Saving..." : "Save follow-up"}
            </button>
          </div>

          {pendingAttachments.length > 0 && (
            <div className="rx-follow-up__attachments">
              {pendingAttachments.map((attachment) => (
                <div className="rx-follow-up__chip" key={attachment.fileKey}>
                  {attachment.previewUrl ? (
                    <img src={attachment.previewUrl} alt={attachment.fileName} />
                  ) : (
                    <i className="fa-solid fa-file-pdf"></i>
                  )}
                  <span className="rx-follow-up__chip-name">{attachment.fileName}</span>
                  <button
                    type="button"
                    className="btn-close btn-close-sm"
                    aria-label="Remove attachment"
                    onClick={() => removePendingAttachment(attachment.fileKey)}
                  ></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">Follow-up History</div>
        <div className="card-body">
          {isLoading && <p className="text-muted mb-0">Loading follow-ups...</p>}

          {!isLoading && entries.length === 0 && (
            <p className="text-muted mb-0">
              No follow-up has been recorded for this Rx yet.
            </p>
          )}

          {!isLoading &&
            entries
              .slice()
              .reverse()
              .map((entry) => (
                <div className="rx-follow-up__entry" key={entry.Id}>
                  <div className="rx-follow-up__entry-head">
                    <div>
                      <strong>{entry.CreatedByName || "Unknown user"}</strong>
                      <span className="text-muted ms-2 small">
                        {formatDateTime(entry.CreatedOn)}
                        {entry.UpdatedOn ? ` (edited ${formatDateTime(entry.UpdatedOn)})` : ""}
                      </span>
                    </div>
                    {canModify(entry) && editingId !== entry.Id && (
                      <div className="d-print-none">
                        <button
                          type="button"
                          className="btn btn-link btn-sm"
                          onClick={() => startEditing(entry)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger"
                          onClick={() => deleteHandler(entry)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>

                  {editingId === entry.Id ? (
                    <div className="d-print-none">
                      <textarea
                        className="form-control"
                        rows={3}
                        value={editingNote}
                        onChange={(event) => setEditingNote(event.target.value)}
                      />
                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-primary btn-sm me-2"
                          onClick={saveEditing}
                          disabled={!editingNote.trim()}
                        >
                          Save changes
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="rx-follow-up__note">{entry.Note}</p>
                  )}

                  {entry.attachments?.length > 0 && (
                    <div className="rx-follow-up__attachments">
                      {entry.attachments.map((attachment) => (
                        <a
                          className="rx-follow-up__chip"
                          key={attachment.Id}
                          href={attachment.url || "#"}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {isImage(attachment.MimeType) && attachment.url ? (
                            <img src={attachment.url} alt={attachment.FileName} />
                          ) : (
                            <i className="fa-solid fa-file-pdf"></i>
                          )}
                          <span className="rx-follow-up__chip-name">
                            {attachment.FileName || "Attachment"}
                          </span>
                        </a>
                      ))}
                    </div>
                  )}

                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

export default RxFollowUpTab;
